import React, { useState, useEffect } from 'react';
import { MessageCircle, Send, User, Heart, Reply } from 'lucide-react';
import Comment from './Comment';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

const CommentSection = ({ itemId, initialComments = [], hideToggle = false, onCommentCountUpdate }) => {
  const { user, isAuthenticated } = useAuth();
  const [comments, setComments] = useState(initialComments);
  
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [fetchingComments, setFetchingComments] = useState(false);

  // Fetch comments when component mounts - always fetch to get accurate count
  useEffect(() => {
    const fetchComments = async () => {
      setFetchingComments(true);
      try {
        const response = await fetch(`https://api.bigclipboard.com/clipboard/${itemId}/comments`);
        
        if (response.ok) {
          const data = await response.json();
          setComments(data.comments || []);
        }
      } catch (error) {
        console.error('Error fetching comments:', error);
      } finally {
        setFetchingComments(false);
      }
    };

    fetchComments();
  }, [itemId]);

  // Update parent component with comment count and data
  useEffect(() => {
    if (onCommentCountUpdate) {
      onCommentCountUpdate(itemId, comments.length, comments);
    }
  }, [comments.length, itemId, onCommentCountUpdate]);

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    
    if (!isAuthenticated) {
      toast.error('Please log in to leave a comment');
      return;
    }
    
    if (!newComment.trim()) {
      toast.error('Please enter a comment');
      return;
    }
    
    setLoading(true);
    try {
      
      // TODO: Replace with actual API call
      const response = await fetch(`https://api.bigclipboard.com/clipboard/${itemId}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({ content: newComment.trim() })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error(`Failed to post comment: ${response.status}`);
      }
      
      const commentData = await response.json();
      
      // Add the new comment to the list
      setComments(prev => [commentData, ...prev]);
      setNewComment('');
      toast.success('💬 Comment posted successfully!', {
        duration: 3000,
        style: {
          background: '#10B981',
          color: '#fff',
          borderRadius: '12px',
          fontSize: '14px',
          fontWeight: '500',
        },
      });
      
    } catch (error) {
      console.error('Error posting comment:', error);
      
      // Temporary fallback for testing - remove this when API is ready
      if (error.message.includes('404') || error.message.includes('Failed to fetch')) {
        const mockComment = {
          id: Date.now(),
          content: newComment.trim(),
          author_name: user?.name || 'You',
          user_id: user?.id || 0,
          created_at: new Date().toISOString()
        };
        setComments(prev => [mockComment, ...prev]);
        setNewComment('');
        toast.success('Comment posted! (Mock - API not ready yet)');
      } else {
        toast.error('Failed to post comment');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteComment = (commentId) => {
    setComments(prev => prev.filter(c => c.id !== commentId));
  };

  return (
    <div className="mt-6 border-t border-gray-100 pt-6 bg-gradient-to-br from-gray-50/50 to-blue-50/30 rounded-2xl p-6">
      {/* Enhanced Comments Toggle - Only show if not hidden */}
      {!hideToggle && (
        <button
          onClick={() => setShowComments(!showComments)}
          className="group flex items-center space-x-3 text-sm font-medium text-gray-700 hover:text-blue-600 hover:bg-blue-50 px-4 py-3 rounded-xl transition-all duration-300 border border-gray-200 hover:border-blue-200 hover:shadow-md hover:scale-[1.02] active:scale-[0.98]"
        >
          <div className="relative">
            <MessageCircle className="h-5 w-5 group-hover:scale-110 transition-transform duration-200" />
            {comments.length > 0 && (
              <span className="absolute -top-2 -right-2 bg-blue-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold">
                {comments.length}
              </span>
            )}
          </div>
          <span className="font-semibold">
            {comments.length} comment{comments.length !== 1 ? 's' : ''}
          </span>
          <span className="text-xs text-gray-500 group-hover:text-blue-500 transition-colors duration-200">
            {showComments ? 'Click to hide' : comments.length === 0 ? 'Be the first to comment!' : 'Click to view and comment'}
          </span>
        </button>
      )}
      
      {(showComments || hideToggle) && (
        <div className="mt-6 space-y-6 animate-in slide-in-from-top-2 duration-300">
          {/* Enhanced Comment Form */}
          {isAuthenticated ? (
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-100 shadow-sm">
              <form onSubmit={(e) => {
                e.preventDefault();
                handleSubmitComment(e);
              }} className="space-y-4">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center shadow-md">
                      <User className="h-5 w-5 text-white" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <textarea
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Share your thoughts..."
                      className="w-full p-4 border border-gray-200 rounded-xl resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white shadow-sm transition-all duration-200 placeholder-gray-400"
                      rows="3"
                      maxLength="500"
                    />
                    <div className="flex items-center justify-between mt-3">
                      <span className="text-xs text-gray-500 font-medium">
                        {newComment.length}/500 characters
                      </span>
                      <button
                        type="button"
                        disabled={loading || !newComment.trim()}
                        onClick={() => handleSubmitComment({ preventDefault: () => {} })}
                        className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm font-medium rounded-lg hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105"
                      >
                        <Send className="h-4 w-4" />
                        <span>{loading ? 'Posting...' : 'Post Comment'}</span>
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-2xl border border-gray-100">
              <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                <MessageCircle className="h-8 w-8 text-gray-400" />
              </div>
              <p className="font-medium text-gray-600">Please log in to leave a comment</p>
              <p className="text-sm text-gray-400 mt-1">Join the conversation!</p>
            </div>
          )}
          
          {/* Enhanced Comments List */}
          <div className="space-y-4">
            {fetchingComments ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 animate-pulse">
                    <div className="flex items-start space-x-3 mb-4">
                      <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
                      <div className="flex-1">
                        <div className="h-4 bg-gray-200 rounded w-24 mb-2"></div>
                        <div className="h-3 bg-gray-200 rounded w-16"></div>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="h-4 bg-gray-200 rounded w-full"></div>
                      <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : comments.length === 0 ? (
              <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-2xl border border-gray-100">
                <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MessageCircle className="h-8 w-8 text-gray-400" />
                </div>
                <p className="font-medium text-gray-600">No comments yet</p>
                <p className="text-sm text-gray-400 mt-1">Be the first to share your thoughts!</p>
              </div>
            ) : (
              <div className="space-y-4">
                {comments.map((comment, index) => (
                  <div 
                    key={comment.id} 
                    className="animate-in"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <Comment 
                      comment={comment} 
                      itemId={itemId} 
                      onDelete={handleDeleteComment} 
                      currentUserId={user?.id} 
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CommentSection; 